import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Upload, FileSpreadsheet, Play, Pause, CheckCircle, XCircle, 
  Clock, AlertTriangle, Trash2, Eye, Download, Plus, RefreshCw,
  BarChart3, Database, Server, Wifi, FileText, ChevronRight,
  Loader2, Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TURPEMassif = () => {
  const navigate = useNavigate();
  const { canCreateRapport } = useAuth();
  
  // États
  const [imports, setImports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  
  // Formulaire de création
  const [newImport, setNewImport] = useState({
    nom: '',
    description: '',
    methode_import: 'csv_upload'
  });
  
  // Upload de fichiers
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Récupérer les imports
      const importsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports`,
        { headers }
      );
      if (importsRes.ok) {
        setImports(await importsRes.json());
      }

      // Récupérer les stats
      const statsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/stats`,
        { headers }
      );
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const createImport = async () => {
    if (!newImport.nom.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newImport)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        setShowCreateModal(false);
        setNewImport({ nom: '', description: '', methode_import: 'csv_upload' });
        // Ouvrir directement l'import créé
        setSelectedImport({ id: result.id, ...newImport, status: 'en_attente', fichiers: [] });
        fetchData();
      }
    } catch (error) {
      console.error('Erreur création import:', error);
    }
  };

  const uploadFiles = async (importId, files) => {
    setUploadingFiles(true);
    const token = localStorage.getItem('token');
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports/${importId}/upload`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          }
        );
      } catch (error) {
        console.error('Erreur upload fichier:', error);
      }
    }
    
    setUploadingFiles(false);
    setSelectedFiles([]);
    fetchData();
    
    // Rafraîchir l'import sélectionné
    if (selectedImport) {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports/${importId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        setSelectedImport(await res.json());
      }
    }
  };

  const startProcessing = async (importId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports/${importId}/start`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Erreur démarrage traitement:', error);
    }
  };

  const deleteImport = async (importId) => {
    if (!window.confirm('Supprimer cet import ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe-massif/imports/${importId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setSelectedImport(null);
      fetchData();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      en_attente: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'En attente' },
      en_cours: { color: 'bg-blue-100 text-blue-700', icon: Loader2, label: 'En cours' },
      termine: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Terminé' },
      erreur: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Erreur' }
    };
    const { color, icon: Icon, label } = config[status] || config.en_attente;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className={`w-3.5 h-3.5 ${status === 'en_cours' ? 'animate-spin' : ''}`} />
        {label}
      </span>
    );
  };

  const getMethodeIcon = (methode) => {
    switch (methode) {
      case 'csv_upload': return <Upload className="w-4 h-4" />;
      case 'api': return <Server className="w-4 h-4" />;
      case 'ftp': return <Wifi className="w-4 h-4" />;
      default: return <FileSpreadsheet className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#397DFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-[#191D1E] tracking-tight">
            TURPE Massif
          </h1>
          <p className="text-[#687175] mt-1">
            Import et analyse en masse de données TURPE
          </p>
        </div>
        {canCreateRapport && canCreateRapport() && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-2.5 px-4 rounded-lg text-sm transition-colors"
            data-testid="create-import-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvel Import
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#F5F6F9] rounded-lg">
                <Database className="w-5 h-5 text-[#687175]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#191D1E]">{stats.total_imports}</div>
                <div className="text-xs text-[#687175]">Total imports</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#191D1E]">{stats.en_attente}</div>
                <div className="text-xs text-[#687175]">En attente</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Loader2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#191D1E]">{stats.en_cours}</div>
                <div className="text-xs text-[#687175]">En cours</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#191D1E]">{stats.termines}</div>
                <div className="text-xs text-[#687175]">Terminés</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-[#397DFF] to-[#4672B6] rounded-lg p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total_pdls_traites}</div>
                <div className="text-xs text-blue-100">PDLs traités</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box - Format de fichier */}
      <div className="bg-[#E8F4FD] border border-[#397DFF] rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#397DFF] mt-0.5" />
          <div>
            <h3 className="font-medium text-[#191D1E]">Format de fichier attendu</h3>
            <p className="text-sm text-[#687175] mt-1">
              Le format exact du fichier CSV sera défini prochainement. Le système est préparé pour accepter 
              des fichiers contenant les données de consommation TURPE avec les informations suivantes : 
              PDL, horodatage, puissance (kW/kVA), et paramètres de contrat.
            </p>
            <div className="flex gap-4 mt-3">
              <div className="flex items-center gap-2 text-sm text-[#687175]">
                <Upload className="w-4 h-4" />
                <span>Upload CSV</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#687175]">
                <Server className="w-4 h-4" />
                <span>API (à venir)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#687175]">
                <Wifi className="w-4 h-4" />
                <span>FTP/SFTP (à venir)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-3 gap-6">
        {/* Liste des imports */}
        <div className="col-span-1 bg-white border border-[#BFC6CC] rounded-lg">
          <div className="px-4 py-3 border-b border-[#BFC6CC] flex items-center justify-between">
            <h2 className="font-semibold text-[#191D1E]">Imports</h2>
            <button onClick={fetchData} className="p-1.5 hover:bg-[#F5F6F9] rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4 text-[#687175]" />
            </button>
          </div>
          
          {imports.length === 0 ? (
            <div className="p-8 text-center text-[#687175]">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
              <p>Aucun import</p>
              {canCreateRapport && canCreateRapport() && (
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="text-[#397DFF] hover:underline mt-2 inline-block text-sm"
                >
                  Créer un premier import
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-[#BFC6CC] max-h-[500px] overflow-y-auto">
              {imports.map((imp) => (
                <button
                  key={imp.id}
                  onClick={() => setSelectedImport(imp)}
                  className={`w-full px-4 py-3 text-left hover:bg-[#F5F6F9] transition-colors ${
                    selectedImport?.id === imp.id ? 'bg-[#E8F4FD]' : ''
                  }`}
                  data-testid={`import-item-${imp.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getMethodeIcon(imp.methode_import)}
                      <span className="font-medium text-[#191D1E] text-sm truncate max-w-[150px]">
                        {imp.nom}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#687175]" />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    {getStatusBadge(imp.status)}
                    <span className="text-xs text-[#687175]">
                      {imp.fichiers?.length || 0} fichier(s)
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Détails de l'import sélectionné */}
        <div className="col-span-2 bg-white border border-[#BFC6CC] rounded-lg">
          {selectedImport ? (
            <>
              <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-[#191D1E]">{selectedImport.nom}</h2>
                  <p className="text-sm text-[#687175]">{selectedImport.description || 'Pas de description'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedImport.status)}
                  {canCreateRapport && canCreateRapport() && (
                    <button
                      onClick={() => deleteImport(selectedImport.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Infos générales */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-[#687175] uppercase tracking-wider mb-1">Méthode</div>
                    <div className="flex items-center gap-2 text-sm text-[#191D1E]">
                      {getMethodeIcon(selectedImport.methode_import)}
                      {selectedImport.methode_import === 'csv_upload' && 'Upload CSV'}
                      {selectedImport.methode_import === 'api' && 'API'}
                      {selectedImport.methode_import === 'ftp' && 'FTP/SFTP'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#687175] uppercase tracking-wider mb-1">Créé le</div>
                    <div className="text-sm text-[#191D1E]">{formatDate(selectedImport.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[#687175] uppercase tracking-wider mb-1">Progression</div>
                    <div className="text-sm text-[#191D1E]">
                      {selectedImport.pdls_traites || 0} / {selectedImport.total_pdls || 0} PDLs
                    </div>
                  </div>
                </div>

                {/* Barre de progression */}
                {selectedImport.status === 'en_cours' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[#687175]">Traitement en cours...</span>
                      <span className="text-sm font-medium text-[#191D1E]">{selectedImport.progress || 0}%</span>
                    </div>
                    <div className="h-2 bg-[#F5F6F9] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#397DFF] rounded-full transition-all duration-300"
                        style={{ width: `${selectedImport.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Zone d'upload si en attente */}
                {selectedImport.status === 'en_attente' && canCreateRapport && canCreateRapport() && (
                  <div>
                    <h3 className="font-medium text-[#191D1E] mb-3">Fichiers à importer</h3>
                    
                    {/* Zone de drop */}
                    <div className="border-2 border-dashed border-[#BFC6CC] rounded-lg p-8 text-center hover:border-[#397DFF] transition-colors">
                      <input
                        type="file"
                        multiple
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                        className="hidden"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 mx-auto mb-3 text-[#BFC6CC]" />
                        <p className="text-[#191D1E] font-medium">
                          Cliquez ou déposez vos fichiers ici
                        </p>
                        <p className="text-sm text-[#687175] mt-1">
                          Formats acceptés : CSV, XLSX
                        </p>
                      </label>
                    </div>

                    {/* Fichiers sélectionnés */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {selectedFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-[#F5F6F9] rounded-lg px-4 py-2">
                            <div className="flex items-center gap-2">
                              <FileSpreadsheet className="w-4 h-4 text-[#687175]" />
                              <span className="text-sm text-[#191D1E]">{file.name}</span>
                              <span className="text-xs text-[#687175]">
                                ({(file.size / 1024).toFixed(1)} Ko)
                              </span>
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => uploadFiles(selectedImport.id, selectedFiles)}
                          disabled={uploadingFiles}
                          className="w-full mt-3 flex items-center justify-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-2.5 px-4 rounded-lg text-sm transition-colors disabled:opacity-50"
                        >
                          {uploadingFiles ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Upload en cours...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Uploader {selectedFiles.length} fichier(s)
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Liste des fichiers uploadés */}
                {selectedImport.fichiers && selectedImport.fichiers.length > 0 && (
                  <div>
                    <h3 className="font-medium text-[#191D1E] mb-3">
                      Fichiers uploadés ({selectedImport.fichiers.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedImport.fichiers.map((fichier, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-[#F5F6F9] rounded-lg px-4 py-3">
                          <div className="flex items-center gap-3">
                            <FileSpreadsheet className="w-5 h-5 text-[#397DFF]" />
                            <div>
                              <div className="text-sm font-medium text-[#191D1E]">{fichier.nom}</div>
                              <div className="text-xs text-[#687175]">
                                {(fichier.taille / 1024).toFixed(1)} Ko • {formatDate(fichier.uploaded_at)}
                              </div>
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                            Uploadé
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bouton de démarrage */}
                {selectedImport.status === 'en_attente' && 
                 selectedImport.fichiers?.length > 0 && 
                 canCreateRapport && canCreateRapport() && (
                  <button
                    onClick={() => startProcessing(selectedImport.id)}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                  >
                    <Play className="w-5 h-5" />
                    Lancer le traitement
                  </button>
                )}

                {/* Résultats si terminé */}
                {selectedImport.status === 'termine' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                      <CheckCircle className="w-5 h-5" />
                      Traitement terminé
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">{selectedImport.pdls_traites}</div>
                        <div className="text-xs text-emerald-600">PDLs traités</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">
                          {selectedImport.resultats?.length || 0}
                        </div>
                        <div className="text-xs text-emerald-600">Analyses générées</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{selectedImport.pdls_erreur}</div>
                        <div className="text-xs text-red-500">Erreurs</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Erreurs si présentes */}
                {selectedImport.erreurs?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                      <AlertTriangle className="w-5 h-5" />
                      Erreurs rencontrées ({selectedImport.erreurs.length})
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedImport.erreurs.map((err, idx) => (
                        <div key={idx} className="text-sm text-red-600 bg-white rounded px-3 py-2">
                          {err.message || JSON.stringify(err)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-[#687175]">
              <Eye className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
              <p>Sélectionnez un import pour voir les détails</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-[#BFC6CC]">
              <h2 className="text-xl font-semibold text-[#191D1E]">Nouvel Import Massif</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-1">
                  Nom de l'import *
                </label>
                <input
                  type="text"
                  value={newImport.nom}
                  onChange={(e) => setNewImport({ ...newImport, nom: e.target.value })}
                  placeholder="Ex: Import Client ABC - Mars 2026"
                  className="w-full px-4 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] focus:border-transparent"
                  data-testid="import-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-1">
                  Description
                </label>
                <textarea
                  value={newImport.description}
                  onChange={(e) => setNewImport({ ...newImport, description: e.target.value })}
                  placeholder="Description optionnelle..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] focus:border-transparent resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">
                  Méthode d'import
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'csv_upload', label: 'Upload CSV', icon: Upload, available: true },
                    { value: 'api', label: 'API', icon: Server, available: false },
                    { value: 'ftp', label: 'FTP/SFTP', icon: Wifi, available: false }
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => method.available && setNewImport({ ...newImport, methode_import: method.value })}
                      disabled={!method.available}
                      className={`p-4 border rounded-lg text-center transition-all ${
                        newImport.methode_import === method.value
                          ? 'border-[#397DFF] bg-[#E8F4FD]'
                          : method.available
                            ? 'border-[#BFC6CC] hover:border-[#397DFF]'
                            : 'border-[#BFC6CC] opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <method.icon className={`w-6 h-6 mx-auto mb-2 ${
                        newImport.methode_import === method.value ? 'text-[#397DFF]' : 'text-[#687175]'
                      }`} />
                      <div className="text-sm font-medium text-[#191D1E]">{method.label}</div>
                      {!method.available && (
                        <div className="text-xs text-[#687175] mt-1">Bientôt</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-[#BFC6CC] flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-[#687175] hover:text-[#191D1E] font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createImport}
                disabled={!newImport.nom.trim()}
                className="px-6 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-create-import"
              >
                Créer l'import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TURPEMassif;
